// @ts-nocheck
/**
 * TB → Balance Sheet result card. A near-exact clone of FinalMappingCardBS,
 * differing only in the equity bucket order: the TB→BS agent produces THREE
 * equity lines (Member's Contributions, Retained Earnings, and the computed
 * Current Year Earnings) where the generic BS agent produces two. Everything
 * else is identical: the classified accounts table, the per-period
 * Assets = Liabilities + Equity balance check (a trial balance self-balances,
 * so the recon table shows mapped / gap / passes — anchor/tie_out are null),
 * and a value-only Excel download. Reuses the fmc-* visual language from
 * Agent.css.
 */
import React, { useMemo, useState } from 'react';
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  Download,
  Loader2,
  FileSpreadsheet,
} from 'lucide-react';

import { downloadAgentMappingAsExcel, SourceFileMissingError } from '../../utils/exportExcel';
import CellReferencesToggle from './CellReferencesToggle';
import './Agent.css';

const fmtCell = (n) => {
  if (n == null || isNaN(n)) return '—';
  const abs = Math.abs(n);
  const formatted = abs.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return n < 0 ? `(${formatted})` : formatted;
};

const fmtMoney = (n) => {
  if (n == null || isNaN(n)) return '—';
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}k`;
  return `${sign}$${abs.toFixed(0)}`;
};

// value_by_period is a positional array aligned to periods[] (new backend
// shape) or a legacy int/str-keyed dict.
const periodKey = (acct, pidx, pos) => {
  const vbp = acct.value_by_period;
  if (Array.isArray(vbp)) return (pos == null ? null : vbp[pos]) ?? null;
  return vbp?.[pidx] ?? vbp?.[String(pidx)] ?? null;
};

// Bucket display order: assets, then liabilities, then the THREE equity lines
// (mirrors src/tb_bs/taxonomy.py DISPLAY_ORDER). Retained Earnings + Current
// Year Earnings replace the BS agent's single "Member's Equity".
const BS_BUCKET_ORDER = [
  'Cash Equivalent',
  'Accounts Receivable',
  'Inventory',
  'Other Current Assets',
  'Fixed Asset',
  'Other Asset',
  'Accounts Payable',
  'Credit Card',
  'Other Current Liability',
  'Long Term Liability',
  "Member's Contributions/(Distributions)",
  'Retained Earnings',
  'Current Year Earnings',
];

const STATUS_META = {
  balanced: { label: 'Balanced', tone: 'good', icon: CheckCircle },
  source_unbalanced: { label: 'Source residual', tone: 'warn', icon: AlertTriangle },
  mapping_error: { label: 'Mapping error', tone: 'bad', icon: XCircle },
  off: { label: 'Off', tone: 'warn', icon: AlertTriangle }, // legacy alias
  error: { label: 'Error', tone: 'bad', icon: XCircle },
  unknown: { label: 'No check', tone: 'muted', icon: AlertTriangle },
};

const FinalMappingCardTBBS = ({ mapping, sessionId }) => {
  const periods = mapping?.periods || [];
  const accounts = mapping?.accounts || [];
  const reconciliation = mapping?.reconciliation || {};
  const periodBreakdowns = mapping?.period_breakdowns || [];
  const status = reconciliation.status || 'unknown';
  const meta = STATUS_META[status] || STATUS_META.unknown;
  const StatusIcon = meta.icon;
  // Gated trial-balance tie-out: debits/credits, and (at point-in-time columns)
  // the source's own debit/credit imbalance plus how exactly we reproduce it.
  const sourceCheck = reconciliation.source_check || null;

  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  // Default ON, matching the P&L mapper. For a trial balance each figure has no
  // single source cell, so the export links every balance-sheet line back to its
  // debit/credit cells in an embedded copy of the TB. Flips off automatically if
  // the source file has been cleaned out of /tmp (see handleDownload).
  const [enableReferencing, setEnableReferencing] = useState(true);

  // Group accounts by bucket, ordered by the balance-sheet bucket order.
  const groups = useMemo(() => {
    const byCat = {};
    for (const a of accounts) {
      const cat = a.category || 'Uncategorized';
      (byCat[cat] = byCat[cat] || []).push(a);
    }
    const idx = (c) => {
      const i = BS_BUCKET_ORDER.indexOf(c);
      return i === -1 ? 999 : i;
    };
    return Object.keys(byCat)
      .sort((a, b) => idx(a) - idx(b) || a.localeCompare(b))
      .map((cat) => ({ category: cat, accounts: byCat[cat] }));
  }, [accounts]);

  const subtotals = useMemo(
    () =>
      groups.map((g) => {
        const out = {};
        periods.forEach((p, pos) => {
          let s = 0;
          for (const a of g.accounts) {
            const v = periodKey(a, p.period_index, pos);
            if (typeof v === 'number') s += v;
          }
          out[p.period_index] = s;
        });
        return out;
      }),
    [groups, periods],
  );

  // Pull mapped Assets/Liabilities/Equity out of a period breakdown's
  // section_rollups (rollup shape: { section, mapped, anchor, tie_out }; for a
  // trial balance anchor/tie_out are null, so we read only `mapped`).
  const sectionRollup = (pb) => {
    const out = { assets: null, liabilities: null, equity: null };
    for (const r of pb?.section_rollups || []) {
      if (r && Object.prototype.hasOwnProperty.call(out, r.section)) {
        out[r.section] = r.mapped;
      }
    }
    return out;
  };

  // Per-period source-debits-vs-credits check. Only meaningful at a point-in-time
  // column (anchor_valid); on a rolling mid-year month Σdr−Σcr is expected non-zero.
  const renderSourceCheck = (pb) => {
    if (!pb.anchor_valid) return <span className="fmc-muted">rolling</span>;
    const si = pb.source_imbalance;
    if (si == null) return <span className="fmc-muted">—</span>;
    return Math.abs(si) <= 1 ? (
      <span className="fmc-recon-badge pass">✓</span>
    ) : (
      <span className="fmc-recon-badge fail">{fmtMoney(si)}</span>
    );
  };

  const handleDownload = async () => {
    if (!sessionId) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      const idxs = periods.map((p) => p.period_index);
      await downloadAgentMappingAsExcel(sessionId, mapping, idxs, enableReferencing);
    } catch (e) {
      if (e instanceof SourceFileMissingError) {
        // Flip the toggle off so a second click succeeds without re-failing.
        setEnableReferencing(false);
        setDownloadError(
          'Source file no longer available — cell references turned off. Click Download again to retry without them.',
        );
      } else {
        setDownloadError(e?.message || String(e));
      }
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="fmc-card">
      <div className="fmc-header">
        <div className="fmc-header-left">
          <FileSpreadsheet size={14} />
          <span className="fmc-filename">
            {mapping?.file?.filename || 'Balance Sheet'}
          </span>
        </div>
        <div className="fmc-header-right">
          <div className={`fmc-status fmc-status-${meta.tone}`}>
            <StatusIcon size={12} />
            <span>{meta.label}</span>
          </div>
        </div>
      </div>

      {/* source_unbalanced is excluded: the conversion is faithful and its
          warn pill + tie-out copy already carry that nuance. */}
      {['mapping_error', 'off', 'error', 'unknown'].includes(status) && (
        <div className="result-notice warn">
          {status === 'unknown'
            ? 'The balance check never ran for this conversion. Review before relying on these figures.'
            : status === 'mapping_error'
              ? 'Some trial-balance accounts were left unmapped, so the converted statement does not balance. Review before relying on these figures.'
              : `This conversion did not reconcile to Assets = Liabilities + Equity (status ${status}${
                  typeof reconciliation.final_discrepancy === 'number' &&
                  reconciliation.final_discrepancy
                    ? `, worst-period gap ${fmtMoney(reconciliation.final_discrepancy)}`
                    : ''
                }). Review before relying on these figures.`}
        </div>
      )}

      <div className="fmc-table-wrap">
        <table className="fmc-table">
          <thead>
            <tr>
              <th className="fmc-col-account">Account</th>
              <th className="fmc-col-category">Category</th>
              {periods.map((p) => (
                <th key={p.period_index} className="fmc-col-period">
                  {p.display_name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((g, gi) => (
              <React.Fragment key={g.category}>
                <tr className="fmc-group-row">
                  <td className="fmc-group-name" colSpan={2}>
                    {g.category}
                    <span className="fmc-group-count">
                      ({g.accounts.length})
                    </span>
                  </td>
                  {periods.map((p) => (
                    <td key={p.period_index} className="fmc-group-total fmc-num">
                      {fmtCell(subtotals[gi][p.period_index])}
                    </td>
                  ))}
                </tr>
                {g.accounts.map((acct) => (
                  <tr
                    key={acct.row_index}
                    className={`fmc-row ${acct.sign_flipped ? 'fmc-sign-flipped' : ''}`}
                  >
                    <td className="fmc-account" title={acct.account_name || ''}>
                      <div className="fmc-account-name">
                        <span className="fmc-account-text">
                          {acct.account_name || (
                            <span className="fmc-muted">—</span>
                          )}
                        </span>
                        {acct.sign_flipped && (
                          <span
                            className="fmc-sign-flipped-tag"
                            title={`Source value was negative; mapped as a positive magnitude (sign swapped).`}
                          >
                            sign flipped
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="fmc-category">
                      {acct.category || 'Uncategorized'}
                    </td>
                    {periods.map((p, pos) => {
                      const v = periodKey(acct, p.period_index, pos);
                      return (
                        <td
                          key={p.period_index}
                          className={`fmc-num ${v < 0 ? 'fmc-neg' : ''}`}
                        >
                          {fmtCell(v)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {periodBreakdowns.length > 0 && (
        <div className="fmc-recon-wrap">
          {sourceCheck && (
            <div className="fmc-tieout">
              <span className="fmc-tieout-label">Trial balance tie-out</span>
              {sourceCheck.basis === 'point_in_time' ? (
                <>
                  <span>
                    Debits <b>{fmtMoney(sourceCheck.total_debit)}</b>
                  </span>
                  <span>
                    Credits <b>{fmtMoney(sourceCheck.total_credit)}</b>
                  </span>
                  <span>
                    Source imbalance{' '}
                    <b
                      className={
                        Math.abs(sourceCheck.source_imbalance ?? 0) > 1
                          ? 'fmc-neg'
                          : ''
                      }
                    >
                      {fmtMoney(sourceCheck.source_imbalance)}
                    </b>
                  </span>
                  <span>
                    Fidelity <b>{fmtMoney(sourceCheck.fidelity)}</b>
                  </span>
                </>
              ) : (
                <span className="fmc-muted">
                  Rolling balance; source debits = credits at{' '}
                  {sourceCheck.anchor_periods?.length || 0} fiscal-year start
                  {sourceCheck.anchor_periods?.length === 1 ? '' : 's'} (per-period
                  columns below).
                </span>
              )}
            </div>
          )}
          <div className="fmc-recon-scroll">
            <table className="fmc-recon-table">
              <thead>
                <tr>
                  <th>Period</th>
                  <th className="fmc-num">Debits</th>
                  <th className="fmc-num">Credits</th>
                  <th className="fmc-num">Assets</th>
                  <th className="fmc-num">Liabilities</th>
                  <th className="fmc-num">Equity</th>
                  <th className="fmc-num">Gap</th>
                  <th>Source check</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {periodBreakdowns.map((pb, idx) => {
                  const r = sectionRollup(pb);
                  const passes = pb.passes !== false;
                  return (
                    <tr key={pb.period_index ?? idx}>
                      <td>{pb.period_display || `Period ${pb.period_index}`}</td>
                      <td className="fmc-num">{fmtMoney(pb.total_debit)}</td>
                      <td className="fmc-num">{fmtMoney(pb.total_credit)}</td>
                      <td className="fmc-num">{fmtMoney(r.assets)}</td>
                      <td className="fmc-num">{fmtMoney(r.liabilities)}</td>
                      <td className="fmc-num">{fmtMoney(r.equity)}</td>
                      <td className={`fmc-num ${passes ? '' : 'fmc-neg'}`}>
                        {fmtMoney(pb.gap)}
                      </td>
                      <td>{renderSourceCheck(pb)}</td>
                      <td>
                        <span
                          className={`fmc-recon-badge ${passes ? 'pass' : 'fail'}`}
                        >
                          {passes ? 'BALANCED' : 'OFF'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="fmc-actions fmc-actions-lg fmc-actions-stack">
        <CellReferencesToggle
          checked={enableReferencing}
          onChange={setEnableReferencing}
          disabled={downloading}
          hint="When on, the downloaded .xlsx embeds a copy of the trial balance and links each balance-sheet figure back to its debit/credit cells via formulas."
        />
        <div className="fmc-export-note">
          {enableReferencing
            ? 'Each balance-sheet figure links back to its debit and credit cells in an embedded copy of the trial balance.'
            : 'Exporting without cell links — values only.'}
        </div>
        <button
          type="button"
          className={`fmc-btn fmc-btn-lg fmc-btn-primary ${downloading ? 'fmc-btn-loading' : ''}`}
          onClick={handleDownload}
          disabled={downloading || !sessionId || periods.length === 0}
        >
          {downloading ? (
            <Loader2 size={14} className="fmc-spin" />
          ) : (
            <Download size={14} />
          )}
          <span className="fmc-btn-label">
            {downloading
              ? 'Generating…'
              : `Download .xlsx · ${periods.length} ${
                  periods.length === 1 ? 'period' : 'periods'
                }`}
          </span>
        </button>
        {downloadError && (
          <span className="fmc-action-error" title={downloadError}>
            {downloadError}
          </span>
        )}
      </div>
    </div>
  );
};

export default FinalMappingCardTBBS;
